import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../AppContext';
import { PageHeader } from '../components/Layout';
import { Card, SectionTitle, Field, Spinner, ErrorNote, RemoveButton, SelectMenu } from '../components/ui';
import { money } from '../lib/format';

const METHODS = ['cash', 'cheque', 'card', 'bank_transfer'];
const REFERENCE_LABELS = { cheque: 'Cheque #', card: 'Transaction #', bank_transfer: 'Transaction #' };

/** A receipt is a record that money has already been received — no balance, no payments to chase. */
export default function ReceiptBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, run } = useApp();
  const { data: project, error, loading, reload } = useAsync(() => api.get('/projects/' + id), [id]);

  const [receipt, setReceipt] = useState(null);
  const [lineItems, setLineItems] = useState([{ description: '', amount: 0 }]);
  const [method, setMethod] = useState(METHODS[0]);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const referenceLabel = REFERENCE_LABELS[method];

  useEffect(() => {
    const inv = project?.invoice;
    if (inv?.kind !== 'receipt') return;
    setReceipt(inv);
    setLineItems(inv.lineItems.map(x => ({ ...x })));
    setMethod(inv.payments?.[0]?.method || METHODS[0]);
    setReference(inv.payments?.[0]?.note || '');
  }, [project]);

  const total = useMemo(() => lineItems.reduce((s, x) => s + (Number(x.amount) || 0), 0), [lineItems]);

  if (loading) return <Spinner label="Loading receipt" />;
  if (error) return <div className="view"><ErrorNote onRetry={reload}>{error}</ErrorNote></div>;

  const patch = (i, k, v) => setLineItems(l => l.map((x, j) => j === i ? { ...x, [k]: v } : x));

  async function save() {
    if (!(total > 0)) { toast('Add an amount first'); return null; }
    const note = referenceLabel ? reference : '';
    const saved = receipt
      ? await api.put('/invoices/' + receipt.id, {
          lineItems, subtotal: total, total, amountPaid: total, balance: 0,
          payments: [{ at: receipt.payments?.[0]?.at || new Date().toISOString(), amount: total, method, note }],
        })
      : await api.post('/invoices/receipt', { projectId: id, lineItems, method, reference: note });
    setReceipt(saved);
    return saved;
  }

  const onPreview = () => run(async () => {
    setBusy(true);
    const r = await save();
    setBusy(false);
    if (r) window.open(`/inv/${r.token}`, '_blank', 'noopener');
  });

  const onSend = () => run(async () => {
    setBusy(true);
    const r = await save();
    if (!r) { setBusy(false); return; }
    const sent = await api.post(`/invoices/${r.id}/send`);
    navigator.clipboard?.writeText(`${location.origin}/inv/${sent.invoice.token}`).catch(() => {});
    setBusy(false);
    if (!sent.email) toast('Link copied — no email on file, so nothing was sent');
    else if (!sent.email.delivered) toast(`Link copied, but the email failed: ${sent.email.error || 'unknown error'}`);
    else toast('Receipt sent — link copied');
    navigate('/documents');
  });

  return (
    <>
      <PageHeader title="Receipt" sub={`${project.number} · ${project.customer?.name || ''}`} back="/documents" />
      <div className="view">
        <Card>
          <SectionTitle>Payment received for</SectionTitle>
          <div className="col" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-3)' }}>
            {lineItems.map((x, i) => (
              <div className="scope-item" key={i}>
                <textarea rows={1} placeholder="Description" aria-label={`Line ${i + 1} description`}
                  value={x.description} onChange={e => patch(i, 'description', e.target.value)} />
                <span className="amt">
                  <input type="number" min="0" step="0.01" placeholder="0" aria-label={`Line ${i + 1} amount`}
                    value={x.amount || ''} onChange={e => patch(i, 'amount', parseFloat(e.target.value || '0'))} />
                </span>
                <RemoveButton label={`Remove line ${i + 1}`}
                  onClick={() => setLineItems(l => l.length > 1 ? l.filter((_, j) => j !== i) : l)} />
              </div>
            ))}
          </div>
          <button type="button" className="btn sm" style={{ marginTop: 'var(--s-3)' }}
            onClick={() => setLineItems(l => [...l, { description: '', amount: 0 }])}>
            ＋ Add line
          </button>
        </Card>

        <Card>
          <SectionTitle>How it was paid</SectionTitle>
          <div className="row-fields" style={{ '--cols': referenceLabel ? 2 : 1, marginTop: 'var(--s-3)' }}>
            <Field label="Method" id="rcmethod">
              <SelectMenu id="rcmethod" value={method}
                onChange={(v) => { setMethod(v); if (!REFERENCE_LABELS[v]) setReference(''); }}
                options={METHODS.map(m => ({ value: m, label: m.replace('_', ' ') }))} />
            </Field>
            {referenceLabel ? (
              <Field label={referenceLabel} id="rcref">
                <input id="rcref" className="fi" value={reference} onChange={e => setReference(e.target.value)} />
              </Field>
            ) : null}
          </div>
        </Card>

        <div className="totals-card">
          <div className="totals-row grand">
            <span className="lbl">Total paid</span>
            <span className="val">{money(total)}</span>
          </div>
        </div>

        <div className="btn-row">
          <button className="btn" onClick={onPreview} disabled={busy}>Save &amp; preview</button>
          <button className="btn pri" onClick={onSend} disabled={busy}>Send to customer</button>
        </div>
      </div>
    </>
  );
}
